import React, { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { WorkOrderStatusCode } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface QuickActionButtonsProps {
  statusId: number;
  onStatusChange: (newStatusId: number, completionNotes?: string, createServiceReport?: boolean) => Promise<void>;
  /** When provided, clicking "Submit for Review" opens the CompletionDialog instead of inline form */
  onRequestCompletion?: () => void;
}

// ─── Shared button base styles ──────────────────────────────
const primaryBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 600,
  border: "none",
  backgroundColor: "#D4572B",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: "8px 18px",
  cursor: "pointer",
  outline: "none",
  whiteSpace: "nowrap" as const,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: "#B84A24",
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "default",
  },
};

const secondaryBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 500,
  border: "1px solid #E8E5DF",
  backgroundColor: "#FFFFFF",
  color: "#D4572B",
  borderRadius: "8px",
  padding: "8px 18px",
  cursor: "pointer",
  outline: "none",
  whiteSpace: "nowrap" as const,
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: "#D1CCC4",
    color: "#B84A24",
    backgroundColor: "#FAFAFA",
  },
};

const QuickActionButtons = ({ statusId, onStatusChange, onRequestCompletion }: QuickActionButtonsProps) => {
  const { translate } = useTranslation("operations");
  const [loadingAction, setLoadingAction] = useState<number | null>(null);

  const handleAction = async (newStatusId: number) => {
    if (newStatusId === WorkOrderStatusCode.PendingReview) {
      if (onRequestCompletion) {
        onRequestCompletion();
        return;
      }
      // Fallback: direct status change without completion form
    }
    setLoadingAction(newStatusId);
    try {
      await onStatusChange(newStatusId);
    } finally {
      setLoadingAction(null);
    }
  };

  const renderButtons = () => {
    switch (statusId) {
      case WorkOrderStatusCode.Assigned:
      case WorkOrderStatusCode.Scheduled:
        return (
          <Box
            component="button"
            onClick={() => handleAction(WorkOrderStatusCode.InProgress)}
            disabled={loadingAction !== null}
            sx={primaryBtnSx}
          >
            {loadingAction === WorkOrderStatusCode.InProgress && <CircularProgress size={14} sx={{ color: "#fff" }} />}
            {translate("myTasks.startWork")}
          </Box>
        );

      case WorkOrderStatusCode.InProgress:
        return (
          <>
            <Box
              component="button"
              onClick={() => handleAction(WorkOrderStatusCode.PendingReview)}
              disabled={loadingAction !== null}
              sx={primaryBtnSx}
            >
              {loadingAction === WorkOrderStatusCode.PendingReview && <CircularProgress size={14} sx={{ color: "#fff" }} />}
              {translate("myTasks.submitForReview")}
            </Box>
            <Box
              component="button"
              onClick={() => handleAction(WorkOrderStatusCode.OnHold)}
              disabled={loadingAction !== null}
              sx={secondaryBtnSx}
            >
              {loadingAction === WorkOrderStatusCode.OnHold && <CircularProgress size={14} sx={{ color: "#D4572B" }} />}
              {translate("myTasks.onHold")}
            </Box>
          </>
        );

      case WorkOrderStatusCode.OnHold:
        return (
          <Box
            component="button"
            onClick={() => handleAction(WorkOrderStatusCode.InProgress)}
            disabled={loadingAction !== null}
            sx={primaryBtnSx}
          >
            {loadingAction === WorkOrderStatusCode.InProgress && <CircularProgress size={14} sx={{ color: "#fff" }} />}
            {translate("myTasks.resume")}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
      {renderButtons()}
    </Box>
  );
};

export default QuickActionButtons;
